import React from "react";
import { Task, TaskStatus } from "../../types/tasks";
import TaskCard from "./TaskCard";
import {
  Box,
  Typography,
  Tooltip,
  Skeleton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useGlobalTheme } from "../../context/GlobalThemeContext";
import { Droppable } from "@hello-pangea/dnd";
import { AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";

import BusinessCenterOutlinedIcon from "@mui/icons-material/BusinessCenterOutlined";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import DoneAllOutlinedIcon from "@mui/icons-material/DoneAllOutlined";

interface Props {
  status: TaskStatus;
  tasks: Task[];
  minHeight?: string | number;
  maxHeight?: string | number;
  onDeleteTask: (taskId: string) => void;
  onTaskClick?: (task: Task) => void;
  loading?: boolean;
}

const icons: Record<TaskStatus, React.ReactElement> = {
  todo: (
    <BusinessCenterOutlinedIcon sx={{ fontSize: "1.1em", color: "#fff" }} />
  ),
  doing: <ArrowForwardOutlinedIcon sx={{ fontSize: "1.1em", color: "#fff" }} />,
  feedback: (
    <ReportProblemOutlinedIcon sx={{ fontSize: "1.1em", color: "#fff" }} />
  ),
  done: <DoneAllOutlinedIcon sx={{ fontSize: "1.1em", color: "#fff" }} />,
};

const columnColors: Record<TaskStatus, string> = {
  todo: "#0585fa", // Azul
  doing: "#e69d00", // Laranja
  feedback: "#54398e", // Roxo
  done: "#328f3c", // Verde
};

const getColumnTitle = (
  statusKey: TaskStatus,
  t: (key: string) => string,
): string => t(`status.${statusKey}`);
const getColumnDescription = (
  status: TaskStatus,
  t: (key: string) => string,
): string => t(`columnDescriptions.${status}`);

const TaskColumn: React.FC<Props> = ({
  status,
  tasks,
  minHeight,
  maxHeight,
  onDeleteTask,
  onTaskClick,
  loading = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = useGlobalTheme();
  const title = getColumnTitle(status, t);
  const description = getColumnDescription(status, t);

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        backgroundColor: isDark ? "rgba(25,25,25,0.95)" : "#ffffff",
        borderRadius: isMobile ? 0 : "0.75em",
        boxShadow: isMobile ? "none" : "0 0.125em 0.5em rgba(0, 0, 0, 0.16)",
        width: "100%",
        minHeight: minHeight || "500px",
        maxHeight: maxHeight || "calc(100vh - 180px)",
        height: isMobile && minHeight === "100%" ? "100%" : "auto",
        overflow: "hidden",
        flex: isMobile && minHeight === "100%" ? 1 : "none",
      }}
    >
      {/* header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: columnColors[status],
          color: "#fff",
          p: 1,
          borderTopLeftRadius: isMobile ? 0 : "0.75em",
          borderTopRightRadius: isMobile ? 0 : "0.75em",
          borderBottom: "1px solid rgba(0,0,0,0.1)",
          flexShrink: 0,
        }}
      >
        <Tooltip title={description} arrow placement="top">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              cursor: "help",
            }}
          >
            {icons[status]}
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="#fff"
              sx={{
                display: { xs: "none", sm: "block" }, // Hide on screens 600px or less
              }}
            >
              {title}
            </Typography>
          </Box>
        </Tooltip>
        <Typography variant="caption" fontWeight={500} color="#fff">
          {t("taskCount", { count: tasks.length })}
        </Typography>
      </Box>

      {/* Content area */}
      <Box
        sx={{
          p: 2,
          flex: 1,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          minHeight: 0,
          maxHeight: "100%",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": { display: "none" },
          backgroundColor: isDark ? "rgba(20,20,20,0.95)" : "#f9f9f9",
        }}
      >
        <Droppable droppableId={status}>
          {(provided) => (
            <Box
              ref={provided.innerRef}
              {...provided.droppableProps}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                width: "100%",
                minHeight: "200px",
              }}
            >
              <AnimatePresence mode="popLayout">
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <Box
                      key={i}
                      sx={{
                        p: 2,
                        backgroundColor: theme.palette.background.paper,
                        borderRadius: "0.5em",
                        boxShadow: 1,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        opacity: 0.3,
                      }}
                    >
                      <Skeleton
                        variant="text"
                        width="60%"
                        sx={{ opacity: 0.3 }}
                      />
                      <Skeleton
                        variant="text"
                        width="80%"
                        sx={{ opacity: 0.3 }}
                      />
                      <Skeleton
                        variant="text"
                        width="40%"
                        sx={{ opacity: 0.3 }}
                      />
                    </Box>
                  ))
                ) : tasks.length === 0 && !loading ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minHeight: "150px",
                      color: "#9ca3af",
                      fontSize: "0.9em",
                      fontStyle: "italic",
                    }}
                  >
                    {t("noTasksInColumn")}
                  </Box>
                ) : (
                  tasks.map((task, index) => (
                    <Box key={task.id}>
                      <TaskCard
                        task={task}
                        status={status}
                        showDeleteIcon={status === "done"}
                        onDelete={() => onDeleteTask(task.id)}
                        onClick={onTaskClick}
                        index={index}
                      />
                    </Box>
                  ))
                )}
              </AnimatePresence>
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
      </Box>
    </Box>
  );
};

export default TaskColumn;
